import React, { useState, useEffect, useCallback } from 'react';

import styled from 'styled-components';

import { useCurrentProject } from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import { ISpeechBubbleIcon, DocumentTextIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import BlockRemoveButton from '../../BlockRemoveButton';
import UnstyledAnchor from '../../UnstyledAnchor';
import MalwareContent from './components/MalwareContent';
import { isBuildPro, isFailedFile } from './helpers/formatEntities';
import { isRfiUpload, getThumbnail, isMalwareDetected } from './helpers/utils';
import { useTranslation } from './i18n';

const StyledAnchor = styled(UnstyledAnchor)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  overflow: hidden;
  padding: 10px;
  border-bottom: 1px solid ${theme.colors.charcoal100};
  cursor: ${({ $isMalware }) => ($isMalware ? 'not-allowed' : 'pointer')};

  ${({ $isRemovable }) =>
    $isRemovable &&
    `
    padding-right: 0;
    &:hover .DocsEntityBlock__remove-button {
      visibility: visible;
    }
  `}
`;

const ThumbnailContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 1px solid ${theme.colors.charcoal200};
  width: 48px;
  height: 48px;
  margin-right: 16px;
`;

const MalwareTitle = styled(Box)`
  min-width: 0;
  max-width: 356px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${theme.colors.charcoal500};
  &:hover {
    color: ${theme.colors.charcoal500};
  }
`;

const fitContentTargetProps = () => ({
  style: { width: 'fit-content' },
});

const DocsEntityBlock = ({
  projectId,
  sourceEntityType,
  entity,
  onRemoveClick,
  onClick,
  onReferenceClickAnalytics,
}) => {
  const { t } = useTranslation();
  const currentProject = useCurrentProject();
  const [thumbnail, setThumbnail] = useState('');

  const scopes = currentProject?.scopes?.project;
  const {
    urn,
    parentFolderUrn,
    version: { bubbleUrn, bubbleViewableGuid } = {},
  } = entity;
  const url = `/${
    isBuildPro() ? 'build' : 'docs'
  }/files/projects/${projectId}?entityId=${urn}&folderUrn=${parentFolderUrn}&moduleId=folders`;
  const showRfiUpload = isRfiUpload(sourceEntityType, entity);
  const isMalware = isMalwareDetected(entity);

  const loadThumbnail = useCallback(async () => {
    if (!bubbleUrn) return;
    const thumbnail = await getThumbnail(bubbleUrn, bubbleViewableGuid, scopes);
    if (thumbnail) setThumbnail(thumbnail);
  }, [bubbleUrn, bubbleViewableGuid, scopes]);

  useEffect(() => {
    !thumbnail && loadThumbnail();
    return () => {
      thumbnail && URL.revokeObjectURL(thumbnail);
    };
  }, [thumbnail, loadThumbnail]);

  const handleClick = () => {
    onReferenceClickAnalytics?.();
    onClick?.(entity);
  };

  if (isFailedFile(entity)) return null;

  return (
    <StyledAnchor
      $isMalware={isMalware}
      $isRemovable={!!onRemoveClick}
      href={!onClick && !isMalware && url}
      onClick={isMalware ? undefined : handleClick}
    >
      <ThumbnailContainer>
        {thumbnail ? (
          <img style={{ width: '100%' }} src={thumbnail} alt="" />
        ) : (
          <DocumentTextIcon
            size={24}
            style={{
              margin: '0 2px',
              ...(isMalware && { color: theme.colors.charcoal500 }),
            }}
          />
        )}
      </ThumbnailContainer>
      <Flex flexDirection="column" flexGrow={1}>
        {isMalware ? (
          <MalwareContent>
            <Tooltip
              content={t('MALWARE_DETECTED_TOOLTIP')}
              getTargetProps={fitContentTargetProps}
            >
              <MalwareTitle>{entity.name}</MalwareTitle>
            </Tooltip>
          </MalwareContent>
        ) : (
          <OverflowTooltip
            getTargetProps={() => ({ style: { overflow: 'hidden', flex: 1 } })}
            content={entity.name}
          >
            {entity.name}
          </OverflowTooltip>
        )}
        {showRfiUpload && (
          <div style={{ width: '20px' }}>
            <Tooltip content={t('RFI_DIRECT_UPLOAD_TOOLTIP')}>
              <ISpeechBubbleIcon size={20} />
            </Tooltip>
          </div>
        )}
      </Flex>
      {onRemoveClick && (
        <Flex
          height="100%"
          alignItems="center"
          justifyContent="center"
          padding="0 15px 0px 0px"
          marginLeft="4px"
        >
          <BlockRemoveButton
            className="DocsEntityBlock__remove-button"
            style={{
              visibility: 'hidden',
            }}
            onClick={() => onRemoveClick(projectId, entity.urn)}
          />
        </Flex>
      )}
    </StyledAnchor>
  );
};

export default DocsEntityBlock;
